import { useMemo, useRef, useState } from 'react';
import { Plus, Pencil, Trash2, History, Eye, EyeOff, Upload, Link2, Check, X, ExternalLink } from 'lucide-react';
import { api } from '../../lib/api';
import { countWord } from '../../lib/countWord.mjs';
import { easternDay } from '../../../modules/q2c/eastern-day.mjs';
import Modal from '../ui/Modal';
import { Chip, fmtDate, inputCls, FieldGrid } from '../bids/FieldGrid';
import { card, iconBtn, primaryBtn, useList, FormModal, HistoryModal, Empty, sourceLabel, fmtDateTime } from './shared';

const SITE = 'https://concria.us';
const SUBTABS = [['leads', 'Website leads'], ['blog', 'Blog'], ['kb', 'Chatbot knowledge'], ['corrections', 'Chatbot corrections']];

export default function WebsiteTab({ options, onChanged }) {
  const [sub, setSub] = useState('leads');
  return (
    <div className="space-y-3">
      <p className="rounded-lg bg-sky-50 p-3 text-xs text-sky-900 dark:bg-sky-900/30 dark:text-sky-100">
        These are the same records concria.us reads. Publishing a blog post or changing the chatbot knowledge changes the live website. Every change is logged with who made it.
      </p>
      <div className="flex flex-wrap gap-1">
        {SUBTABS.map(([k, l]) => <button key={k} onClick={() => setSub(k)} className={`rounded-full px-3 py-1 text-xs ${sub === k ? 'bg-primary text-white' : 'border border-[var(--border)]'}`}>{l}</button>)}
      </div>
      {sub === 'leads' && <LeadsPanel options={options} onChanged={onChanged} />}
      {sub === 'blog' && <BlogPanel />}
      {sub === 'kb' && <KbPanel />}
      {sub === 'corrections' && <CorrectionsPanel />}
    </div>
  );
}

// ── leads ─────────────────────────────────────────────────────────────────────
function LinkModal({ lead, options, onDone, onClose }) {
  const s = lead.suggestion || {};
  const [v, setV] = useState({ account_id: s.account_id || null, contact_id: s.contact_id || null });
  const [err, setErr] = useState('');
  const fields = useMemo(() => [
    { key: 'account_id', label: 'CRM company', type: 'picker', options: options.accounts.map((a) => ({ value: a.id, label: a.name })), span: 2 },
    { key: 'contact_id', label: 'CRM contact', type: 'picker', options: options.contacts.map((c) => ({ value: c.id, label: `${c.name}${c.email ? ` <${c.email}>` : ''}` })), span: 2 },
  ], [options]);
  const go = async (body) => { try { await api.post(`/marketing/site/leads/${lead.id}/link`, body); onDone(); } catch (e) { setErr(e.message); } };
  return (
    <Modal title={`Link ${lead.name || lead.email} to the CRM`} onClose={onClose}>
      <div className="mb-3 text-sm text-[var(--text-secondary)]">{lead.company || 'No company given'} · {lead.email || 'no email'}</div>
      {lead.suggestion && <p className="mb-3 text-xs">Suggested match ({lead.suggestion.how}): {[lead.suggestion.account_name, lead.suggestion.contact_name].filter(Boolean).join(' / ')}</p>}
      <FieldGrid fields={fields} value={v} onChange={setV} />
      {err && <p className="mt-2 text-sm text-rose-600">{err}</p>}
      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <button onClick={() => go({ create_account: !!lead.company, create_contact: true })} className="rounded-lg border border-[var(--border)] px-3 py-1.5 text-sm" title="Uses an existing match when there is one">Create company and contact</button>
        <button disabled={!v.account_id && !v.contact_id} onClick={() => go({ account_id: v.account_id, contact_id: v.contact_id })} className={primaryBtn}><Link2 size={14} /> Link</button>
      </div>
    </Modal>
  );
}

function LeadsPanel({ options, onChanged }) {
  const [origin, setOrigin] = useState('website');
  const { rows, err, load, setErr } = useList(`/marketing/site/leads?origin=${origin}`, 'leads');
  const [link, setLink] = useState(null);
  const [msg, setMsg] = useState('');
  const [onlyUnlinked, setOnlyUnlinked] = useState(false);
  const shown = (rows || []).filter((l) => !onlyUnlinked || !l.linked);
  const unlinkedWithMatch = (rows || []).filter((l) => !l.linked && l.suggestion).length;
  // Keep late bulk-link answers aligned with the origin currently shown.
  const shownNow = useRef({ origin, load });
  shownNow.current = { origin, load };
  async function linkExact() {
    // This line reports the last bulk link only.
    // Anything newer clears it.
    setMsg('');
    setErr('');
    try {
      const r = await api.post('/marketing/site/leads/link-exact', { origin });
      // Another origin is shown now: refresh its list and say nothing about this one.
      if (shownNow.current.origin !== origin) { await shownNow.current.load(); onChanged?.(); return; }
      setMsg(`Linked ${r.linked} lead${r.linked === 1 ? '' : 's'}.${r.skipped.length ? ` ${r.skipped.length} skipped.` : ''}`); await load(); onChanged?.();
    } catch (e) { setErr(e.message); }
  }
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <select className={`${inputCls} w-auto`} value={origin} onChange={(e) => { setOrigin(e.target.value); setMsg(''); }}>
            <option value="website">From the website</option><option value="event">From events</option><option value="portal">Entered in portal</option><option value="other">Other sources</option><option value="all">All</option>
          </select>
          <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={onlyUnlinked} onChange={(e) => setOnlyUnlinked(e.target.checked)} /> Only not linked to CRM</label>
        </div>
        <button className={primaryBtn} disabled={!unlinkedWithMatch} onClick={linkExact}><Link2 size={14} /> Link {unlinkedWithMatch} exact match{unlinkedWithMatch === 1 ? '' : 'es'}</button>
      </div>
      <p className="text-xs text-[var(--text-secondary)]">Website leads already appear on the Leads board. Linking ties each one to its CRM company and contact (matched on email, then business web domain, then company name) so it counts toward deals. Nothing on the website changes.</p>
      {msg && <p className="text-sm text-emerald-700 dark:text-emerald-300">{msg}</p>}
      {err && <p className="text-sm text-rose-600">{err}</p>}
      {!rows && !err && <p className="text-sm text-[var(--text-secondary)]">Loading…</p>}
      {rows && !shown.length && <Empty>No leads here.</Empty>}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-[var(--text-secondary)]"><th className="py-1 pr-2">Received</th><th className="pr-2">Who</th><th className="pr-2">Source / first touch</th><th className="pr-2">Stage</th><th className="pr-2">CRM</th></tr></thead>
          <tbody>
            {shown.map((l) => (
              <tr key={l.id} className="border-t border-[var(--border)] align-top">
                <td className="py-2 pr-2 text-xs">{fmtDate(easternDay(l.created_at))}</td>
                <td className="pr-2"><div className="font-medium">{l.name || '(no name)'}</div><div className="text-xs text-[var(--text-secondary)]">{[l.company, l.email, l.phone].filter(Boolean).join(' · ')}</div>
                  {l.message && <div className="mt-0.5 line-clamp-2 text-xs text-[var(--text-secondary)]">{l.message}</div>}
                  {l.same_email_leads > 1 && <div className="text-xs text-amber-700 dark:text-amber-300">{l.same_email_leads} leads from this email</div>}</td>
                <td className="pr-2 text-xs">{sourceLabel(l.source)}{(l.utm_source || l.utm_campaign) && <div className="text-[var(--text-secondary)]">{[l.utm_source, l.utm_medium, l.utm_campaign].filter(Boolean).join(' / ')}</div>}
                  {l.landing_page && <div className="max-w-[16rem] truncate text-[var(--text-secondary)]" title={l.landing_page}>{l.landing_page}</div>}</td>
                <td className="pr-2 text-xs">{l.stage}</td>
                <td className="pr-2 text-xs">
                  {l.linked ? <Chip tone="green">{l.account_name || 'Linked'}</Chip> : (
                    <div className="space-y-1">
                      {l.suggestion && <div className="text-[var(--text-secondary)]">Match: {[l.suggestion.account_name, l.suggestion.contact_name].filter(Boolean).join(' / ')}</div>}
                      <button className={iconBtn} onClick={() => setLink(l)}><Link2 size={12} /> Link</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {link && <LinkModal lead={link} options={options} onClose={() => setLink(null)} onDone={async () => { setLink(null); setMsg(''); await load(); onChanged?.(); }} />}
    </div>
  );
}

// ── blog ──────────────────────────────────────────────────────────────────────
const BLOG_FIELDS = [
  { key: 'title', label: 'Title', required: true, span: 2 },
  { key: 'slug', label: 'Web address', required: true, hint: 'concria.us/blog/<this>. Lowercase words joined by hyphens.' },
  { key: 'category', label: 'Category' },
  { key: 'excerpt', label: 'Summary', type: 'textarea', rows: 2, hint: 'Shown on the blog list and in search results.' },
  { key: 'image', label: 'Image path', span: 2, hint: 'A site path such as /images/blog/photo.webp' },
  { key: 'content', label: 'Article (markdown)', type: 'textarea', rows: 14, required: true, hint: 'Use ## and ### headings, - bullets, **bold** and [text](https://...) links.' },
  { key: 'published', label: 'Published on the website', type: 'bool', checkLabel: 'Visible on concria.us/blog' },
];

function BlogPanel() {
  const { rows, err, load, setErr } = useList('/marketing/site/blog', 'posts');
  const [edit, setEdit] = useState(null);
  const [hist, setHist] = useState(null);
  const save = async (payload) => {
    if (edit.id) await api.patch(`/marketing/site/blog/${edit.id}`, payload); else await api.post('/marketing/site/blog', payload);
    setEdit(null); await load();
  };
  const act = async (fn) => { try { await fn(); await load(); } catch (e) { setErr(e.message); } };
  return (
    <div className="space-y-2">
      <div className="flex justify-end"><button className={primaryBtn} onClick={() => setEdit({ published: false })}><Plus size={14} /> New post</button></div>
      {err && <p className="text-sm text-rose-600">{err}</p>}
      {!rows && !err && <p className="text-sm text-[var(--text-secondary)]">Loading…</p>}
      {rows && !rows.length && <Empty>No blog posts yet.</Empty>}
      {(rows || []).map((p) => (
        <div key={p.id} className={card}>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2"><Chip tone={p.published ? 'green' : 'gray'}>{p.published ? 'Published' : 'Not published'}</Chip>{p.category && <span className="text-xs text-[var(--text-secondary)]">{p.category}</span>}<span className="text-xs text-[var(--text-secondary)]">{fmtDate(easternDay(p.created_at))}</span></div>
              <div className="mt-1 font-medium">{p.title}</div>
              <div className="text-xs text-[var(--text-secondary)]">/blog/{p.slug}</div>
            </div>
            <div className="flex flex-wrap gap-1">
              {p.published && <a className={iconBtn} href={`${SITE}/blog/${p.slug}`} target="_blank" rel="noreferrer noopener"><ExternalLink size={12} /> View</a>}
              <button className={iconBtn} onClick={() => setEdit(p)}><Pencil size={12} /> Edit</button>
              <button className={iconBtn} onClick={() => { if (!p.published && !window.confirm(`Publish "${p.title}" on concria.us/blog now?`)) return; act(() => api.patch(`/marketing/site/blog/${p.id}`, { published: !p.published })); }}>{p.published ? <><EyeOff size={12} /> Unpublish</> : <><Eye size={12} /> Publish</>}</button>
              <button className={iconBtn} onClick={() => setHist(p)}><History size={12} /></button>
              <button className={iconBtn} onClick={() => { if (window.confirm(`Delete "${p.title}" from the website? A copy is kept in the history log.`)) act(() => api.delete(`/marketing/site/blog/${p.id}`)); }}><Trash2 size={12} /></button>
            </div>
          </div>
        </div>
      ))}
      {edit && <FormModal title={edit.id ? 'Edit blog post' : 'New blog post'} fields={BLOG_FIELDS} record={edit} onSave={save} onClose={() => setEdit(null)} />}
      {hist && <HistoryModal table="blog_posts" id={hist.id} title={hist.title} onClose={() => setHist(null)} />}
    </div>
  );
}

// ── knowledge base ────────────────────────────────────────────────────────────
function KbPanel() {
  const { rows, err, load, setErr } = useList('/marketing/site/kb', 'documents');
  const [adding, setAdding] = useState(false);
  const [view, setView] = useState(null);
  const [hist, setHist] = useState(null);
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addErr, setAddErr] = useState('');
  async function add() {
    setBusy(true); setAddErr('');
    try {
      if (file) {
        const fd = new FormData(); fd.append('file', file); if (title) fd.append('title', title);
        const res = await fetch('/api/marketing/site/kb/upload', { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('mickey_token')}` }, body: fd });
        // An expired session goes to sign-in, as the shared api helper does, instead of the refusal's words.
        if (res.status === 401) { localStorage.removeItem('mickey_token'); window.location.href = '/login'; const err = new Error('Unauthorized'); err.status = 401; throw err; }
        const j = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(j.error || `Upload failed (${res.status})`);
      } else {
        await api.post('/marketing/site/kb', { title, content: text });
      }
      setAdding(false); setTitle(''); setText(''); setFile(null); await load();
    } catch (e) { if (e.status !== 401) setAddErr(e.message); }
    setBusy(false);
  }
  async function open(d) { try { const r = await api.get(`/marketing/site/kb/${d.id}`); setView(r.document); } catch (e) { setErr(e.message); } }
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-[var(--text-secondary)]">Documents the concria.us chatbot searches to answer visitors. Keep this to product facts cleared for the public: no prices.</p>
        <button className={primaryBtn} onClick={() => setAdding(true)}><Plus size={14} /> Add document</button>
      </div>
      {err && <p className="text-sm text-rose-600">{err}</p>}
      {!rows && !err && <p className="text-sm text-[var(--text-secondary)]">Loading…</p>}
      {rows && !rows.length && <Empty>No documents.</Empty>}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <tbody>
            {(rows || []).map((d) => (
              <tr key={d.id} className="border-t border-[var(--border)]">
                <td className="py-2 pr-2"><button className="text-left font-medium hover:underline" onClick={() => open(d)}>{d.title}</button>{d.protected && <span className="ml-2"><Chip tone="indigo">Approved corrections</Chip></span>}<div className="text-xs text-[var(--text-secondary)]">{d.file_name} · {fmtDate(easternDay(d.created_at))}</div></td>
                <td className="whitespace-nowrap text-right">
                  <button className={iconBtn} onClick={() => setHist(d)}><History size={12} /></button>{' '}
                  {!d.protected && <button className={iconBtn} onClick={async () => { if (!window.confirm(`Remove "${d.title}" from the chatbot? A copy is kept in the history log.`)) return; try { await api.delete(`/marketing/site/kb/${d.id}`); await load(); } catch (e) { setErr(e.message); } }}><Trash2 size={12} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {adding && (
        <Modal title="Add a chatbot document" onClose={() => setAdding(false)} maxWidth="max-w-3xl">
          <div className="grid gap-3">
            <label className="text-sm"><span className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">Title</span><input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} /></label>
            <label className="text-sm"><span className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">Upload a file (.md, .txt, .pdf, .docx, max 5 MB)</span><input type="file" accept=".md,.markdown,.txt,.json,.csv,.pdf,.docx" onChange={(e) => setFile(e.target.files?.[0] || null)} /></label>
            {!file && <label className="text-sm"><span className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">Or paste the text</span><textarea className={inputCls} rows={10} value={text} onChange={(e) => setText(e.target.value)} /></label>}
          </div>
          {addErr && <p className="mt-2 text-sm text-rose-600">{addErr}</p>}
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setAdding(false)} className="rounded-lg border border-[var(--border)] px-3 py-1.5 text-sm">Cancel</button>
            <button disabled={busy || (!file && !text.trim())} onClick={add} className={primaryBtn}><Upload size={14} /> {busy ? 'Adding…' : 'Add to chatbot'}</button>
          </div>
        </Modal>
      )}
      {view && (
        <Modal title={view.title} onClose={() => setView(null)} maxWidth="max-w-4xl">
          <div className="mb-2 text-xs text-[var(--text-secondary)]">{view.file_name} · {view.chunks.length} searchable {countWord(view.chunks.length, 'section')}</div>
          <div className="max-h-[60vh] space-y-2 overflow-auto">
            {view.chunks.map((c) => <pre key={c.id} className="whitespace-pre-wrap rounded-lg bg-gray-50 p-2 font-sans text-xs dark:bg-slate-800">{c.content}</pre>)}
          </div>
        </Modal>
      )}
      {hist && <HistoryModal table="knowledge_documents" id={hist.id} title={hist.title} onClose={() => setHist(null)} />}
    </div>
  );
}

// ── corrections ───────────────────────────────────────────────────────────────
function CorrectionsPanel() {
  const [status, setStatus] = useState('pending');
  const { rows, err, load, setErr } = useList(`/marketing/site/corrections?status=${status}`, null);
  const [approve, setApprove] = useState(null);
  const openApprove = useRef(null);
  const [approveErr, setApproveErr] = useState('');
  const [answer, setAnswer] = useState('');
  const [type, setType] = useState('wording');
  function closeApprove() {
    openApprove.current = null;
    setApprove(null);
  }
  async function decide(id, body) {
    const isApprove = body.action === 'approve';
    if (isApprove) setApproveErr('');
    else setErr('');
    try {
      await api.post(`/marketing/site/corrections/${id}`, body);
      closeApprove();
      await load();
    } catch (e) {
      // An approval's failure shows in its own open dialog; one whose dialog was cancelled
      // (or replaced by another correction's) shows on the list line, as a reject's does.
      if (isApprove && openApprove.current?.id === id) setApproveErr(e.message);
      else setErr(e.message);
    }
  }
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {['pending', 'approved', 'rejected', 'all'].map((s) => <button key={s} onClick={() => setStatus(s)} className={`rounded-full px-3 py-1 text-xs capitalize ${status === s ? 'bg-primary text-white' : 'border border-[var(--border)]'}`}>{s}</button>)}
        {rows && <span className="text-xs text-[var(--text-secondary)]">{rows.pendingCount} waiting for review</span>}
      </div>
      <p className="text-xs text-[var(--text-secondary)]">Reviewers suggest better chatbot answers on the website (with ?reviewer=1). Approving adds the answer to the chatbot's knowledge right away.</p>
      {err && <p className="text-sm text-rose-600">{err}</p>}
      {!rows && !err && <p className="text-sm text-[var(--text-secondary)]">Loading…</p>}
      {rows && !rows.corrections.length && <Empty>Nothing here.</Empty>}
      {(rows?.corrections || []).map((c) => (
        <div key={c.id} className={card}>
          <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--text-secondary)]"><Chip tone={c.status === 'approved' ? 'green' : c.status === 'rejected' ? 'red' : 'amber'}>{c.status}</Chip>{c.correction_type === 'incorrect' ? 'Wrong answer' : 'Better wording'} · from {c.reviewer_name} · {fmtDateTime(c.created_at)}</div>
          {c.user_question && <div className="mt-2 text-sm"><span className="text-[var(--text-secondary)]">Question:</span> {c.user_question}</div>}
          <div className="mt-1 text-sm"><span className="text-[var(--text-secondary)]">Chatbot said:</span> {c.original_answer}</div>
          <div className="mt-1 text-sm"><span className="text-[var(--text-secondary)]">Suggested:</span> {c.corrected_answer}</div>
          {c.correction_reason && <div className="mt-1 text-xs text-[var(--text-secondary)]">Why: {c.correction_reason}</div>}
          {c.approved_answer && <div className="mt-1 text-sm"><span className="text-[var(--text-secondary)]">Approved answer:</span> {c.approved_answer} <span className="text-xs text-[var(--text-secondary)]">({c.approved_by})</span></div>}
          {c.status === 'pending' && (
            <div className="mt-2 flex gap-1">
              <button className={iconBtn} onClick={() => { setApprove(c); openApprove.current = c; setApproveErr(''); setAnswer(c.corrected_answer || ''); setType(c.correction_type || 'wording'); }}><Check size={12} /> Review and approve</button>
              <button className={iconBtn} onClick={() => decide(c.id, { action: 'reject' })}><X size={12} /> Reject</button>
            </div>
          )}
        </div>
      ))}
      {approve && (
        <Modal title="Approve chatbot correction" onClose={closeApprove}>
          <label className="text-sm"><span className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">Answer the chatbot should give</span><textarea className={inputCls} rows={5} value={answer} onChange={(e) => setAnswer(e.target.value)} /></label>
          <div className="mt-2 flex gap-4 text-sm">
            <label className="flex items-center gap-1"><input type="radio" checked={type === 'incorrect'} onChange={() => setType('incorrect')} /> The old answer was wrong (overrides other sources)</label>
            <label className="flex items-center gap-1"><input type="radio" checked={type === 'wording'} onChange={() => setType('wording')} /> Better wording only</label>
          </div>
          {approveErr && <p className="mt-2 text-sm text-rose-600">{approveErr}</p>}
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={closeApprove} className="rounded-lg border border-[var(--border)] px-3 py-1.5 text-sm">Cancel</button>
            <button disabled={!answer.trim()} onClick={() => decide(approve.id, { action: 'approve', approved_answer: answer, correction_type: type })} className={primaryBtn}>Approve</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
